import { createContext, memo, useCallback, useContext, useMemo } from 'react';

import { cn } from '../../lib/styles';

interface TabsContextType {
  activeTab: number;
  setActiveTab: (index: number) => void;
}

const TabsContext = createContext<TabsContextType | null>(null);

function useTabsContext() {
  const context = useContext(TabsContext);
  if (!context) throw new Error('Tab components must be used within a Tabs component');
  return context;
}

interface TabData {
  label: string;
  value?: string;
}

interface TabProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  index?: number;
}

export const TabBase = ({ children, index = 0, className, ...rest }: TabProps) => {
  const { activeTab } = useTabsContext();
  const isActive = activeTab === index;

  return (
    <button
      type="button"
      className={cn(
        'relative h-14 px-2 font-heading-l font-semibold transition-all truncate',
        'cursor-pointer hover:text-text-primary rounded-tl-sm rounded-tr-sm hover:bg-bg-inter-secondary-default',
        {
          'text-text-primary border-b-2 border-bg-inter-primary-default': isActive,
          'text-text-tertiary border-b border-transparent ': !isActive,
        },
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
};

export const Tab = memo(TabBase);

interface TabsProps {
  id?: string;
  children?: React.ReactNode;
  data?: string[] | TabData[];
  className?: string;
  value?: string;
  onChange?: (tab: string) => void;
}

const TabsBase = ({ id, children, data, className, value, onChange }: TabsProps) => {
  const activeTab = useMemo(() => {
    if (value)
      return data?.findIndex((item) => (typeof item === 'string' ? item === value : item.value === value)) || 0;

    return 0;
  }, [data, value]);

  const setActiveTab = useCallback(
    (index: number) => {
      const tab = data?.[index];

      if (typeof tab === 'string') return onChange?.(tab);

      if (!tab || !tab.value) return;

      onChange?.(tab.value);
    },
    [data, onChange],
  );

  const content = useMemo(() => {
    if (data) {
      return (
        <>
          {data.map((item, index) => {
            const label = typeof item === 'string' ? item : item.label;

            return (
              <Tab key={index} index={index} onClick={() => setActiveTab(index)}>
                {label}
              </Tab>
            );
          })}
        </>
      );
    }

    return children;
  }, [data, children, setActiveTab]);

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div id={id} className={cn('w-full px-4 flex border-b border-border-secondary', className)}>
        {content}
      </div>
    </TabsContext.Provider>
  );
};

export const Tabs = memo(TabsBase);
